<template>
  <div class="flex justify-center">


    <div class="w-full">
      <div class="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-6 gap-4">
        <div class="max-w-2xl">
          <h2 class="text-secondary-600 text-xl font-bold">Weekly Assignment List</h2>
          <p class="text-[13px] text-gray-500 mt-0.5 leading-relaxed">
            Assignments below are automatically available to coders and will rollover if not completed by the end of the week.
          </p>
        </div>
        <div class="flex items-center gap-2 self-start lg:self-center">
          <!-- CSV Import -->
          <button @click="triggerFileInput" 
                  class="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-md border border-green-200 text-green-600 hover:text-green-700 hover:border-green-300 hover:bg-green-50 transition-all text-xs font-semibold shadow-sm bg-white" 
                  title="Import assignments from CSV">
            <i class="fas fa-file-csv text-sm"></i>
            <span class="hidden md:inline text-[11px]">Import CSV</span>
            <i class="fas fa-question-circle text-[10px] opacity-40 hover:opacity-100 ml-0.5" @click.stop="showCsvExample = true"></i>
          </button>

          <!-- Column Settings -->
          <div class="relative inline-block text-left" ref="settingsContainer">
            <button @click.stop="showSettings = !showSettings" 
                    class="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-500 hover:text-logo hover:border-logo hover:bg-logo/5 transition-all text-xs font-semibold shadow-sm bg-white" 
                    title="Column Visibility">
              <i class="fas fa-columns text-sm transition-transform duration-300" :class="{'rotate-180': showSettings}"></i>
              <span class="hidden md:inline text-[11px]">Columns</span>
              <i class="fas fa-chevron-down text-[8px] opacity-50"></i>
            </button>
            <div v-if="showSettings" class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
              <div class="py-1 px-3 space-y-2">
                <div class="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b pb-1 mb-1">Columns</div>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" v-model="visibleCols.submitted" @change="saveSettings" class="rounded text-logo focus:ring-logo">
                  <span class="text-xs text-gray-700">Added Date</span>
                </label>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" v-model="visibleCols.completed" @change="saveSettings" class="rounded text-logo focus:ring-logo">
                  <span class="text-xs text-gray-700">Completed</span>
                </label>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" v-model="visibleCols.problems" @change="saveSettings" class="rounded text-logo focus:ring-logo">
                  <span class="text-xs text-gray-700">Problems</span>
                </label>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" v-model="visibleCols.comments" @change="saveSettings" class="rounded text-logo focus:ring-logo">
                  <span class="text-xs text-gray-700">Comments</span>
                </label>
                <label v-if="!agency.review_disabled" class="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" v-model="visibleCols.review" @change="saveSettings" class="rounded text-logo focus:ring-logo">
                  <span class="text-xs text-gray-700">Review</span>
                </label>
                <label v-if="!agency.poc_disabled" class="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" v-model="visibleCols.poc" @change="saveSettings" class="rounded text-logo focus:ring-logo">
                  <span class="text-xs text-gray-700">POC</span>
                </label>
              </div>
            </div>
          </div>

          <a :href="'/agencies/' + agency_id" class="better-btn bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200 px-3 py-1.5 text-xs font-bold rounded-md">
            Dashboard
          </a>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col">
        <!-- New Assignment Entry Row (Now at the very top) -->
        <div class="bg-gray-50 border-b border-logo/20 p-2.5">
            <!-- Consolidated Entry Row -->
            <div class="flex flex-col lg:flex-row gap-2 items-stretch">
                <!-- Name -->
                <div class="flex-[3] min-w-0">
                    <input ref="newName" type="text" v-model="assign_data.name" placeholder="Patient Name" class="input small h-9 w-full" tabindex="1" @keyup.enter="assign(false)" />
                </div>
                
                <!-- Type & Date (Grouped on smaller screens) -->
                <div class="flex-[4] flex gap-2">
                    <div class="flex-1">
                        <select v-model="assign_data.document_type" class="input small h-9 w-full" tabindex="2">
                            <option disabled value="">Type</option>
                            <option>SOC</option>
                            <option>ROC</option>
                            <option>Recert</option>
                            <option>ROC/Recert</option>
                            <option>Follow-up</option>
                            <option>SNA</option>
                        </select>
                    </div>
                    <div class="flex-1">
                        <input type="date" v-model="assign_data.date" class="input small h-9 w-full px-2" tabindex="3" @keyup.enter="assign(false)" />
                    </div>
                </div>

                <!-- Comments -->
                <div class="flex-[3] relative flex items-center min-w-0">
                    <input type="text" v-model="assign_data.comments" placeholder="Comments (optional)" class="input small h-9 w-full pr-8" tabindex="4" @keyup.enter="assign(false)" />
                    <button type="button" @click="triggerEntryAttachment" class="absolute right-2 text-gray-400 hover:text-logo transition-colors" title="Attach Files" tabindex="5">
                        <i class="fas fa-paperclip text-xs"></i>
                    </button>
                    <input type="file" ref="entryAttachmentInput" class="hidden" multiple @change="handleEntryAttachment" />
                </div>

                <!-- Review / POC Upcharges -->
                <div class="flex items-center gap-3 px-1 whitespace-nowrap">
                    <label v-if="!agency.review_disabled && !reviewUsesDefaults" class="flex items-center space-x-1.5 cursor-pointer" :title="reviewTooltip">
                        <input type="checkbox" name="review" v-model="assign_data.review" class="rounded text-logo focus:ring-logo h-3.5 w-3.5" tabindex="6" />
                        <span class="text-[10px] text-gray-700">Review <span class="italic text-logo">+${{ pricing ? pricing.review : 20 }}</span></span>
                    </label>
                    <label v-if="!agency.poc_disabled && !pocUsesDefaults" class="flex items-center space-x-1.5 cursor-pointer" :title="pocTooltip">
                        <input type="checkbox" name="poc" v-model="assign_data.poc" class="rounded text-logo focus:ring-logo h-3.5 w-3.5" tabindex="7" />
                        <span class="text-[10px] text-gray-700">POC <span class="italic text-logo">+${{ pricing ? pricing.poc : 10 }}</span></span>
                    </label>
                </div>

                <!-- Action Buttons -->
                <div class="flex-[2] flex shadow-sm rounded overflow-hidden min-w-[160px]">
                    <button type="button" @click="assign(false, false)" class="flex-1 bg-logo text-white px-2 h-9 hover:bg-logo-dark transition-colors font-bold text-[11px] flex items-center justify-center whitespace-nowrap" tabindex="8">
                        <i class="fas fa-plus mr-1.5"></i> Add Task
                    </button>
                    <button type="button" @click="assign(false, true)" class="w-10 bg-rose-500 text-white hover:bg-rose-600 border-l border-white/20 transition-colors flex items-center justify-center" :title="urgentTooltip" tabindex="9">
                        <i class="fas fa-fire text-xs"></i>
                    </button>
                    <button type="button" @click="assign(true, false)" class="w-10 bg-orange-500 text-white hover:bg-orange-600 border-l border-white/20 transition-colors flex items-center justify-center" title="Add on Hold" tabindex="10">
                        <i class="fas fa-pause text-xs"></i>
                    </button>
                </div>
            </div>
            
            <!-- Attachment Previews (only if they exist) -->
            <div v-if="entryAttachments.length" class="flex flex-wrap gap-2 mt-2 px-1">
                <div v-for="(file, idx) in entryAttachments" :key="idx" 
                     class="relative group bg-white border border-gray-200 rounded shadow-sm flex items-center space-x-2 px-2 py-1 max-w-[200px] cursor-pointer hover:border-logo transition-all"
                     @click="viewLocalFile(file)"
                     title="Click to view">
                    <i :class="getFileIcon(file.name)" class="text-gray-400 text-[10px]"></i>
                    <span class="text-[9px] truncate font-medium text-gray-600">{{ file.name }}</span>
                    <button @click.stop="removeEntryAttachment(idx)" class="text-gray-400 hover:text-red-500 transition-colors ml-auto pl-2">
                        <i class="fas fa-times text-[10px]"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Scrollable Table Section -->
        <div class="overflow-auto scrollbar-thin flex-1 min-h-0 max-h-[500px]">
          <div class="min-w-max relative">
            <!-- Table Header -->
            <div class="grid gap-0 bg-gray-100 p-0 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b sticky top-0 z-10 shadow-sm items-stretch h-10" :style="gridStyle">
              <div class="px-4 flex items-center border-r border-gray-200/50">Patient Name</div>
              <div class="px-2 flex items-center border-r border-gray-200/50">Type</div>
              <div class="px-2 flex items-center border-r border-gray-200/50">Date</div>
              <div class="px-2 flex items-center justify-center border-r border-gray-200/50">Hold</div>
              <div v-if="showReviewCol" class="px-1 flex items-center justify-center border-r border-gray-200/50" :title="reviewTooltip">Rev</div>
              <div v-if="showPocCol" class="px-1 flex items-center justify-center border-r border-gray-200/50" :title="pocTooltip">POC</div>
              <div v-if="visibleCols.submitted" class="px-4 flex items-center border-r border-gray-200/50">Added</div>
              <div v-if="visibleCols.completed" class="px-4 flex items-center border-r border-gray-200/50">Completed</div>
              <div v-if="visibleCols.problems" class="px-4 flex items-center text-red-500 border-r border-gray-200/50">Problem Detail</div>
              <div v-if="visibleCols.comments" class="px-4 flex items-center border-r border-gray-200/50">Comments</div>
              <div class="px-4 relative flex items-center group border-r border-gray-200/50" ref="statusFilterContainer">
                <span>Status</span>
                <button @click.stop="showStatusFilter = !showStatusFilter" 
                        class="ml-2 flex items-center justify-center h-5 px-1.5 rounded-full transition-all duration-300 ring-1 ring-inset"
                        :class="selectedStatuses.length > 0 
                          ? 'bg-logo text-white ring-logo shadow-md scale-105' 
                          : 'text-gray-400 hover:text-logo ring-transparent hover:ring-gray-300'">
                  <i class="fas fa-filter text-[8px]"></i>
                  <span v-if="selectedStatuses.length > 0" class="ml-1 text-[9px] font-bold">{{ selectedStatuses.length }}</span>
                </button>
                
                <div v-if="showStatusFilter" 
                     class="absolute top-full left-0 mt-1 w-48 bg-white rounded shadow-xl border border-gray-200 z-[60] normal-case tracking-normal p-2 font-normal">
                  <div class="flex justify-between items-center mb-2 border-b pb-1">
                    <span class="text-[9px] font-bold text-gray-400 uppercase">Filter Status</span>
                    <button @click="selectedStatuses = []" class="text-[9px] text-logo hover:underline">Clear</button>
                  </div>
                  <div class="space-y-1 max-h-48 overflow-y-auto">
                    <label v-for="status in uniqueStatuses" :key="status" class="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                      <input type="checkbox" v-model="selectedStatuses" :value="status" class="rounded text-logo focus:ring-logo h-3 w-3">
                      <span class="text-[10px] text-gray-700 whitespace-nowrap">{{ status }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div class="flex items-center justify-end px-4">Options</div>
            </div>

            <!-- Table Body -->
            <div class="min-h-0">
          <!-- Existing Assignments -->
          <div v-for="(assignment, index) in sortedAssignments" :key="assignment.id" 
               class="grid gap-0 text-[10px] items-stretch border-b hover:bg-blue-50 transition-colors"
               :style="gridStyle"
               :class="{ 
                 'bg-gray-50/50': index % 2 === 1 && !assignment.has_open_qa && !assignment.on_hold, 
                 'bg-white': index % 2 === 0 && !assignment.has_open_qa && !assignment.on_hold,
                 'bg-purple-50/40': assignment.has_open_qa,
                 'bg-orange-50/30': assignment.on_hold && !assignment.has_open_qa,
                 'opacity-90': assignment.on_hold && !assignment.has_open_qa
               }">
            <div class="font-medium truncate py-1 px-4 border-r border-gray-100 flex items-center" 
                 :class="{'text-red-600': assignment.problem}"
                 @dblclick="tryEdit(assignment, 'name')"
                 title="Double-click to edit name">
               <template v-if="editing.id === assignment.id && editing.field === 'name'">
                 <input type="text" v-model="assignment.name" 
                        class="w-full text-[10px] p-0 border-none outline-none bg-blue-100 font-medium" 
                        @blur="updateField(assignment, 'name')" 
                        @keyup.enter="updateField(assignment, 'name')"
                        @keyup.esc="cancelEdit(assignment)"
                        v-focus />
               </template>
               <template v-else>
                 {{ assignment.name }}
                 <i v-if="assignment.urgent" class="fas fa-fire text-rose-500 ml-1 text-[9px]" title="Urgent"></i>
                 <i v-if="assignment.on_hold" class="fas fa-pause-circle text-orange-400 ml-1 text-[9px]" title="On Hold"></i>
                 <i v-if="assignment.attachments.length > 0" 
                    @click.stop="!assignment.has_open_qa && toggleFileUpload(assignment.id)" 
                    class="fas fa-paperclip ml-1 cursor-pointer text-secondary-400 hover:text-logo text-[9px]" 
                    :class="{'opacity-50 cursor-not-allowed': assignment.has_open_qa}"
                    :title="assignment.has_open_qa ? 'Disabled during QA Review' : 'Manage Attachments'"></i>
               </template>
            </div>
            <div class="py-1 px-2 border-r border-gray-100 flex items-center" @dblclick="tryEdit(assignment, 'document_type')">
                <template v-if="editing.id === assignment.id && editing.field === 'document_type'">
                    <select v-model="assignment.document_type" 
                            class="w-full text-[10px] p-0 border-none outline-none bg-blue-100" 
                            @change="updateField(assignment, 'document_type')"
                            @blur="cancelEdit(assignment)" @keyup.esc="cancelEdit(assignment)"
                            v-focus>
                        <option>SOC</option>
                        <option>ROC</option>
                        <option>Recert</option>
                        <option>ROC/Recert</option>
                        <option>Follow-up</option>
                        <option>SNA</option>
                    </select>
                </template>
                <template v-else>{{ assignment.document_type }}</template>
            </div>
            <div class="py-1 px-2 border-r border-gray-100 whitespace-nowrap flex items-center" @dblclick="tryEdit(assignment, 'date')">
                <template v-if="editing.id === assignment.id && editing.field === 'date'">
                    <input type="date" v-model="assignment.date" 
                           class="w-full text-[10px] p-0 border-none outline-none bg-blue-100" 
                           @blur="updateField(assignment, 'date')" 
                           @keyup.enter="updateField(assignment, 'date')"
                           @keyup.esc="cancelEdit(assignment)"
                           v-focus />
                </template>
                <template v-else>{{ format_short_date(assignment.date) }}</template>
            </div>
            <div class="py-1 border-r border-gray-100 flex items-center justify-center">
              <input type="checkbox" 
                     :checked="assignment.on_hold" 
                     @change="toggleHold(assignment)" 
                     class="rounded text-orange-500 focus:ring-orange-400 disabled:bg-gray-200 disabled:border-gray-300 disabled:cursor-not-allowed h-3 w-3" 
                     :disabled="assignment.has_open_qa || (assignment.status !== 'Not Started' && !assignment.on_hold)"
                     :title="assignment.has_open_qa ? 'Managed by QA Review' : ''" />
            </div>
            <div v-if="showReviewCol" class="py-1 border-r border-gray-100 flex items-center justify-center">
              <input type="checkbox" :id="'review-' + assignment.id"
                     :checked="assignment.review"
                     @change="toggleAddon(assignment, 'review')"
                     class="rounded text-logo focus:ring-logo disabled:bg-gray-200 disabled:border-gray-300 disabled:cursor-not-allowed h-3 w-3"
                     :disabled="!canToggleAddon(assignment)"
                     :title="canToggleAddon(assignment) ? reviewTooltip : 'Locked once coding has started'" />
            </div>
            <div v-if="showPocCol" class="py-1 border-r border-gray-100 flex items-center justify-center">
              <input type="checkbox" :id="'poc-' + assignment.id"
                     :checked="assignment.poc"
                     @change="toggleAddon(assignment, 'poc')"
                     class="rounded text-logo focus:ring-logo disabled:bg-gray-200 disabled:border-gray-300 disabled:cursor-not-allowed h-3 w-3"
                     :disabled="!canToggleAddon(assignment)"
                     :title="canToggleAddon(assignment) ? pocTooltip : 'Locked once coding has started'" />
            </div>
            <div v-if="visibleCols.submitted" class="py-1 px-4 border-r border-gray-100 truncate flex items-center" :title="assignment.created_at">
               {{ fromNow(assignment.created_at) }}
            </div>
            <div v-if="visibleCols.completed" class="py-1 px-4 border-r border-gray-100 flex items-center">
               {{ assignment.completed_at ? fromNow(assignment.completed_at) : '-' }}
            </div>
            <div v-if="visibleCols.problems" class="py-1 px-4 border-r border-gray-100 flex flex-col space-y-2 relative overflow-hidden" :class="{'bg-red-50/20': assignment.problem}">
               <template v-if="assignment.problem_id">
                 <!-- Previous Problems (collapsible) -->
                 <div v-if="previousProblems(assignment).length > 0">
                   <button type="button" @click="toggleProblemHistory(assignment.id)"
                           class="w-full text-left text-[8px] uppercase tracking-wider font-bold text-gray-400 hover:text-gray-600 flex items-center space-x-1 py-0.5">
                     <i class="fas text-[8px]" :class="isProblemHistoryOpen(assignment.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                     <span>{{ previousProblems(assignment).length }} previous problem{{ previousProblems(assignment).length === 1 ? '' : 's' }}</span>
                   </button>
                   <div v-if="isProblemHistoryOpen(assignment.id)" class="mt-1 space-y-1.5 pl-2 border-l border-gray-200">
                     <div v-for="prob in previousProblems(assignment)" :key="prob.id" class="text-[9px]">
                       <div class="font-bold uppercase text-[7px] text-red-400 tracking-wider mb-0.5">Coder Observation <span class="font-normal text-gray-400 normal-case tracking-normal">· {{ prob.created_ago }}</span></div>
                       <div class="italic text-gray-700 leading-tight">{{ prob.comment || 'No detail' }}</div>
                       <template v-if="prob.resolved_comment">
                         <div class="font-bold uppercase text-[7px] text-green-600 tracking-wider mt-1 mb-0.5">Agency Resolution <span v-if="prob.resolved_ago" class="font-normal text-gray-400 normal-case tracking-normal">· {{ prob.resolved_ago }}</span></div>
                         <div class="italic text-gray-600 leading-tight">{{ prob.resolved_comment }}</div>
                       </template>
                     </div>
                   </div>
                 </div>

                 <!-- Coder Observation (latest) -->
                 <div class="relative pl-2 border-l border-red-500 py-0.5 mt-0.5">
                   <div class="font-bold uppercase text-[7px] text-red-500 tracking-wider mb-0.5">Coder Observation</div>
                   <div class="italic text-gray-800 leading-tight text-[10px]">{{ assignment.problem_comment || 'No detail' }}</div>
                 </div>

                 <!-- Agency Resolution Input -->
                 <div v-if="assignment.problem" class="mt-1 pb-1">
                   <textarea v-model="assignment.resolved_comment"
                             class="w-full p-2 border border-blue-100 rounded-t text-[10px] focus:ring-1 focus:ring-logo focus:border-logo outline-none transition-all leading-tight h-14 bg-white shadow-sm resize-none"
                             placeholder="Enter resolution details..."></textarea>
                   <button @click="resolveProblem(assignment)"
                           :disabled="resolving === assignment.id"
                           class="w-full bg-green-500 hover:bg-green-600 border border-green-600/10 text-white font-bold py-1.5 px-2 rounded-b text-[8px] shadow-sm active:translate-y-[1px] transition-all flex items-center justify-center uppercase tracking-widest">
                      <i v-if="resolving === assignment.id" class="fas fa-spinner fa-spin mr-1.5"></i>
                      <i v-else class="fas fa-check mr-1.5"></i>
                     Mark Resolved
                   </button>
                 </div>

                 <!-- Resolved Case -->
                 <div v-else-if="assignment.resolved_comment" class="relative pl-2 border-l border-green-500 py-0.5 bg-green-50/30 rounded-r">
                   <div class="font-bold uppercase text-[7px] text-green-600 tracking-wider mb-0.5">Agency Resolution</div>
                   <div class="text-gray-700 leading-tight italic">{{ assignment.resolved_comment }}</div>
                 </div>
               </template>
               <template v-else>
                 <span class="text-gray-300 italic opacity-50">-</span>
               </template>
            </div>
            <div v-if="visibleCols.comments" class="py-1 px-4 border-r border-gray-100 flex flex-col justify-center space-y-1" @dblclick="tryEdit(assignment, 'comments')">
               <!-- Agency Comment (editable) - hidden when empty and not editing -->
               <div v-if="assignment.comments || (editing.id === assignment.id && editing.field === 'comments')"
                    class="relative pl-2 border-l border-logo/40 py-0.5" :title="assignment.comments">
                 <div class="font-bold uppercase text-[7px] text-logo tracking-wider mb-0.5">Agency</div>
                 <template v-if="editing.id === assignment.id && editing.field === 'comments'">
                   <input type="text" v-model="assignment.comments"
                          class="w-full text-[10px] p-0 border-none outline-none bg-blue-100 italic"
                          @blur="updateField(assignment, 'comments')"
                          @keyup.enter="updateField(assignment, 'comments')"
                          @keyup.esc="cancelEdit(assignment)"
                          v-focus />
                 </template>
                 <template v-else>
                   <div class="italic text-gray-600 leading-tight text-[10px]">{{ assignment.comments }}</div>
                 </template>
               </div>
               <!-- Coder Comment (read-only) -->
               <div v-if="assignment.coder_comment" class="relative pl-2 border-l border-logo-alt2/60 py-0.5" :title="assignment.coder_comment">
                 <div class="font-bold uppercase text-[7px] text-logo-alt2 tracking-wider mb-0.5">Coder</div>
                 <div class="italic text-gray-600 leading-tight text-[10px]">{{ assignment.coder_comment }}</div>
               </div>
            </div>
            <div class="py-1 px-4 border-r border-gray-100 flex items-center">
               <span class="text-[9px] font-bold uppercase tracking-wider whitespace-nowrap" :class="{
                'text-green-600': assignment.status.includes('Completed'),
                'text-orange-500': assignment.status === 'Not Started' || assignment.on_hold,
                'text-orange-700': assignment.status.includes('Started') || assignment.status.includes('Sent to Problems'),
                'text-gray-400': assignment.status.includes('Order Not Yet Submitted'),
                'text-purple-600': assignment.has_open_qa
              }">
                {{ assignment.has_open_qa ? 'Internal Review' : (assignment.on_hold ? 'HOLD' : assignment.status) }}
                <span v-if="assignment.coder_initials" class="text-gray-500 font-normal ml-0.5">({{ assignment.coder_initials }})</span>
              </span>
            </div>
            <div class="col-span-1 flex items-center justify-end space-x-2 py-1 px-4">
              <i v-if="canToggleUrgent(assignment)" @click="toggleUrgent(assignment)"
                 class="fas fa-fire cursor-pointer text-xs"
                 :class="assignment.urgent ? 'text-rose-500 hover:text-rose-300' : 'text-gray-300 hover:text-rose-400'"
                 :title="assignment.urgent ? 'Remove Urgent' : 'Mark Urgent'"></i>
              <i @click="!assignment.has_open_qa && toggleFileUpload(assignment.id)"
                 class="fas fa-paperclip cursor-pointer text-gray-300 hover:text-logo text-xs"
                 :class="{'opacity-50 cursor-not-allowed': assignment.has_open_qa}"
                 title="Add Attachments"></i>
              <i v-if="canReopen(assignment)" @click="toggleReopen(assignment.id)"
                 class="fas fa-undo cursor-pointer text-gray-300 hover:text-yellow-500 text-xs"
                 title="Reopen Assignment"></i>
              <i v-if="canDelete(assignment)" @click="!assignment.has_open_qa && remove(assignment.id)"
                 class="fas fa-trash cursor-pointer text-gray-300 hover:text-red-500 text-xs"
                 :class="{'opacity-50 cursor-not-allowed': assignment.has_open_qa}"
                 title="Remove Item"></i>
            </div>
            
            <!-- Inline Reopen area -->
            <div v-if="reopenTarget === assignment.id" class="col-span-12 p-3 bg-yellow-50/50 border-t">
              <div class="flex flex-col space-y-2">
                <div class="text-[10px] font-bold text-yellow-700 uppercase tracking-wider">Reopen Assignment</div>
                <textarea v-model="reopenComment"
                          class="w-full p-2 border border-yellow-200 rounded text-[11px] focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400 outline-none transition-all leading-tight h-14 bg-white shadow-sm resize-none"
                          placeholder="Enter reason for reopening..."></textarea>
                <div class="flex items-center space-x-2">
                  <button @click="reopenAssignment(assignment)"
                          :disabled="!reopenComment.trim() || reopening === assignment.id"
                          class="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-1.5 px-3 rounded text-[10px] shadow-sm transition-all flex items-center uppercase tracking-widest">
                    <i v-if="reopening === assignment.id" class="fas fa-spinner fa-spin mr-1.5"></i>
                    <i v-else class="fas fa-undo mr-1.5"></i>
                    Reopen
                  </button>
                  <button @click="reopenTarget = null; reopenComment = ''" class="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                </div>
              </div>
            </div>

            <!-- Inline Management area -->
            <div v-if="showFileUpload === assignment.id" class="col-span-12 p-3 bg-blue-50/50 border-t">
               <div class="flex flex-col space-y-3">
                  <div class="flex items-center space-x-4">
                     <file-upload name="attachments" class="flex-1" @loaded="file => handleFileUpload(file, assignment.id)"></file-upload>
                     <button type="button" class="text-xs text-secondary-500 hover:text-secondary-800" @click="cancelAttach">Close</button>
                  </div>
                  
                  <!-- Existing Attachments in Management Area -->
                  <div v-if="assignment.attachments.length" class="flex flex-wrap gap-3 pt-2 border-t border-blue-100">
                     <div v-for="(doc, docI) in assignment.attachments" :key="doc.id" 
                          class="relative group bg-white border border-gray-200 rounded shadow flex flex-col w-24 overflow-hidden cursor-pointer hover:border-logo transition-all"
                          @click="viewUploadedFile(doc, assignment.id)"
                          v-bind:title="doc.original + ' (Click to view)'">
                         <div class="w-full h-16 flex items-center justify-center bg-gray-50 overflow-hidden">
                             <img v-if="isImageDoc(doc)" :src="getUploadedFileUrl(doc, assignment.id)" class="w-full h-full object-cover" />
                             <div v-else class="text-gray-400 flex flex-col items-center">
                                 <i :class="getFileIcon(doc.original)" class="text-xl"></i>
                             </div>
                         </div>
                         <div class="p-1 border-t bg-white">
                             <div class="text-[8px] truncate font-bold text-gray-700 px-1" :title="doc.original">{{ doc.original }}</div>
                             <div class="text-[7px] text-gray-400 px-1 uppercase">{{ getFileExt(doc.original) }}</div>
                         </div>
                         <div class="absolute top-0 right-0 bg-red-500 text-white w-4 h-4 rounded-bl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" @click.stop="removeAttachment(doc.id, docI, assignment.id)">
                             <i class="fas fa-times text-[10px]"></i>
                         </div>
                     </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>

        <!-- Footer -->
        <div class="bg-gray-50 p-3 border-t flex justify-end items-center text-xs text-gray-500 italic">
           <div>
              Estimated Total: <span class="font-bold text-logo">{{ total | currency }}</span>
           </div>
        </div>

        <!-- Previous Weekly Orders -->
        <div v-if="previousOrders && previousOrders.length" class="border-t">
          <div v-for="prevOrder in previousOrders" :key="'order-' + prevOrder.id" class="border-b last:border-b-0">
            <button @click="togglePreviousOrder(prevOrder.id)"
                    class="w-full flex items-center justify-between px-4 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors">
              <div class="flex items-center space-x-2 font-semibold">
                <i class="fas fa-chevron-right transition-transform duration-200" :class="{'rotate-90': expandedOrders.includes(prevOrder.id)}"></i>
                <span>Order #{{ prevOrder.order_number }}</span>
                <span class="text-gray-400 font-normal">{{ prevOrder.assignments.length }} assignments</span>
              </div>
              <span class="text-gray-400">Charged {{ fromNow(prevOrder.charged_at) }}</span>
            </button>
            <div v-if="expandedOrders.includes(prevOrder.id)">
              <div class="overflow-auto max-h-[400px]">
                <div class="min-w-max">
                  <div class="grid gap-0 bg-gray-100 p-0 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b items-stretch h-10" :style="gridStyle">
                    <div class="px-4 flex items-center border-r border-gray-200/50">Patient Name</div>
                    <div class="px-2 flex items-center border-r border-gray-200/50">Type</div>
                    <div class="px-2 flex items-center border-r border-gray-200/50">Date</div>
                    <div class="px-2 flex items-center justify-center border-r border-gray-200/50">Hold</div>
                    <div v-if="showReviewCol" class="px-1 flex items-center justify-center border-r border-gray-200/50">Rev</div>
                    <div v-if="showPocCol" class="px-1 flex items-center justify-center border-r border-gray-200/50">POC</div>
                    <div v-if="visibleCols.submitted" class="px-4 flex items-center border-r border-gray-200/50">Added</div>
                    <div v-if="visibleCols.completed" class="px-4 flex items-center border-r border-gray-200/50">Completed</div>
                    <div v-if="visibleCols.problems" class="px-4 flex items-center border-r border-gray-200/50">Problem Detail</div>
                    <div v-if="visibleCols.comments" class="px-4 flex items-center border-r border-gray-200/50">Comments</div>
                    <div class="px-4 flex items-center border-r border-gray-200/50">Status</div>
                    <div class="flex items-center justify-end px-4">Options</div>
                  </div>
                  <div v-for="(assignment, index) in prevOrder.assignments" :key="'prev-' + assignment.id"
                       class="grid gap-0 text-[10px] items-stretch border-b"
                       :class="index % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'"
                       :style="gridStyle">
                    <div class="font-medium truncate py-1 px-4 border-r border-gray-100 flex items-center">{{ assignment.name }}</div>
                    <div class="py-1 px-2 border-r border-gray-100 flex items-center">{{ assignment.document_type }}</div>
                    <div class="py-1 px-2 border-r border-gray-100 whitespace-nowrap flex items-center">{{ format_short_date(assignment.date) }}</div>
                    <div class="py-1 border-r border-gray-100 flex items-center justify-center">
                      <i v-if="assignment.on_hold" class="fas fa-pause-circle text-orange-400 text-[9px]"></i>
                      <span v-else class="text-gray-300">-</span>
                    </div>
                    <div v-if="showReviewCol" class="py-1 border-r border-gray-100 flex items-center justify-center">
                      <i v-if="assignment.review" class="fas fa-check text-logo text-[9px]" title="With Review"></i>
                      <span v-else class="text-gray-300">-</span>
                    </div>
                    <div v-if="showPocCol" class="py-1 border-r border-gray-100 flex items-center justify-center">
                      <i v-if="assignment.poc" class="fas fa-check text-logo text-[9px]" title="With POC"></i>
                      <span v-else class="text-gray-300">-</span>
                    </div>
                    <div v-if="visibleCols.submitted" class="py-1 px-4 border-r border-gray-100 truncate flex items-center">{{ fromNow(assignment.created_at) }}</div>
                    <div v-if="visibleCols.completed" class="py-1 px-4 border-r border-gray-100 flex items-center">{{ assignment.completed_at ? fromNow(assignment.completed_at) : '-' }}</div>
                    <div v-if="visibleCols.problems" class="py-1 px-4 border-r border-gray-100 flex items-center text-gray-400 italic">-</div>
                    <div v-if="visibleCols.comments" class="py-1 px-4 border-r border-gray-100 italic truncate text-gray-400 flex items-center">{{ assignment.comments || '-' }}</div>
                    <div class="py-1 px-4 border-r border-gray-100 flex items-center">
                      <span class="text-[9px] font-bold uppercase tracking-wider" :class="{
                        'text-green-600': assignment.completed_at,
                        'text-orange-500': !assignment.completed_at
                      }">
                        {{ assignment.completed_at ? 'Completed' : 'Not Completed' }}
                        <span v-if="assignment.coder_initials" class="text-gray-500 font-normal ml-0.5">({{ assignment.coder_initials }})</span>
                      </span>
                    </div>
                    <div class="py-1 px-4 flex items-center justify-end">
                      <i v-if="assignment.completed_at" @click="toggleReopen('prev-' + assignment.id)"
                         class="fas fa-undo cursor-pointer text-gray-300 hover:text-yellow-500 text-xs"
                         title="Reopen Assignment"></i>
                      <span v-else class="text-gray-300">-</span>
                    </div>

                    <!-- Inline Reopen area for previous order assignments -->
                    <div v-if="reopenTarget === 'prev-' + assignment.id" class="col-span-12 p-3 bg-yellow-50/50 border-t">
                      <div class="flex flex-col space-y-2">
                        <div class="text-[10px] font-bold text-yellow-700 uppercase tracking-wider">Reopen Assignment</div>
                        <textarea v-model="reopenComment"
                                  class="w-full p-2 border border-yellow-200 rounded text-[11px] focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400 outline-none transition-all leading-tight h-14 bg-white shadow-sm resize-none"
                                  placeholder="Enter reason for reopening..."></textarea>
                        <div class="flex items-center space-x-2">
                          <button @click="reopenPreviousAssignment(assignment)"
                                  :disabled="!reopenComment.trim() || reopening === assignment.id"
                                  class="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-1.5 px-3 rounded text-[10px] shadow-sm transition-all flex items-center uppercase tracking-widest">
                            <i v-if="reopening === assignment.id" class="fas fa-spinner fa-spin mr-1.5"></i>
                            <i v-else class="fas fa-undo mr-1.5"></i>
                            Reopen
                          </button>
                          <button @click="reopenTarget = null; reopenComment = ''" class="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  </div>

    <!-- Hidden CSV Input -->
    <input type="file" ref="csvInput" accept=".csv" class="hidden" @change="handleCsvUpload" />

    <csv-example-modal 
      :show="showCsvExample"
      :date-formats="dateFormats"
      :document-type-map="documentTypeMap"
      @close="showCsvExample = false"
    />
  </div>
</template>

<script>
import moment from "moment";
import BackButton from "./BackButton.vue";
import FileUpload from "./FileUpload.vue";
import CsvExampleModal from './CsvExampleModal.vue'

export default {
  components: { 
    BackButton, 
    FileUpload,
    CsvExampleModal
  },
  props: ["agency", "orderTotal", "previousOrders", "pricing"],
  data() {
    return {
      agency_id: this.agency.id,
      order_id: this.agency.incomplete_order.id,
      assignments: this.agency.incomplete_order.assignments,
      total: this.orderTotal,
      showFileUpload: "",
      assign_data: {
        name: "",
        document_type: "",
        date: "",
        comments: "",
        review: 0,
        poc: 0,
        urgent: 0,
        pro: 0,
        on_hold: 0,
      },
      showCsvExample: false,
      entryAttachments: [],
      showSettings: false,
      resolving: null,
      reopenTarget: null,
      reopenComment: '',
      reopening: null,
      editing: { id: null, field: null, originalValue: null },
      selectedStatuses: [],
      showStatusFilter: false,
      expandedOrders: [],
      visibleCols: {
        submitted: true,
        completed: true,
        problems: false,
        comments: false,
        review: true,
        poc: true
      },
      expandedProblems: []
    };
  },
  mounted() {
    this.loadSettings();
    document.addEventListener('keydown', this.handleGlobalKeyDown);
    document.addEventListener('click', this.handleGlobalClick);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleGlobalKeyDown);
    document.removeEventListener('click', this.handleGlobalClick);
  },
  methods: {
    assign(isOnHold = false, isUrgent = false) {
      if (!this.assign_data.name || !this.assign_data.document_type || !this.assign_data.date) {
        this.$toast.error("Please fill in all required fields (Name, Type, and Date).");
        return;
      }

      this.assign_data.on_hold = isOnHold ? 1 : 0;
      this.assign_data.urgent = isUrgent ? 1 : 0;

      axios
        .post(`/agencies/${this.agency_id}/orders/${this.order_id}/assignments`, this.assign_data)
        .then(({ data }) => {
          // If the server moved us to a new order (old one was charged), reload to sync
          if (data.order_id && data.order_id !== this.order_id) {
            this.$toast.info("Previous order was billed. Reloading to sync up.");
            setTimeout(() => window.location.reload(), 1500);
            return;
          }
          // Capture attachments to upload before resetting
          const attachmentsToUpload = [...this.entryAttachments];
          this.resetEntry();
          // Review/POC selections persist for the next entry, matching OrderPage
          this.assign_data.review = data.review;
          this.assign_data.poc = data.poc;
          this.assignments.push(data);
          this.total += parseFloat(data.cost || 0);
          this.$toast.success("Assignment added to bucket.");
          if (attachmentsToUpload.length) {
            this.uploadEntryAttachments(data.id, attachmentsToUpload);
          }
          this.$nextTick(() => {
            if (this.$refs.newName) this.$refs.newName.focus();
          });
        })
        .catch((e) => {
          this.$toast.error(e.response.data.errors[0][0]);
        });
    },
    remove(id) {
      axios.delete(`/assignments/${id}`).then(() => {
        const index = this.assignments.findIndex(a => a.id === id);
        if (index !== -1) {
          this.assignments.splice(index, 1);
        }
        this.reTotal();
      });
    },
    reTotal() {
      this.total = this.assignments.reduce((sum, a) => sum + parseFloat(a.cost || 0), 0);
    },
    resetEntry() {
      this.assign_data = { 
        name: "", 
        document_type: "", 
        date: "",
        comments: "",
        review: 0,
        poc: 0,
        urgent: 0,
        pro: 0,
        on_hold: 0,
      };
      this.entryAttachments = [];
    },
    format_date(d) {
      return moment.utc(d).format("MMM DD, YYYY");
    },
    format_short_date(d) {
      return moment.utc(d).format("MM/DD/YY");
    },
    fromNow(d) {
      return moment.utc(d).fromNow();
    },
    saveSettings() {
      localStorage.setItem('weekly_order_cols', JSON.stringify(this.visibleCols));
    },
    loadSettings() {
      const saved = localStorage.getItem('weekly_order_cols');
      if (saved) {
        try {
          this.visibleCols = { ...this.visibleCols, ...JSON.parse(saved) };
        } catch (e) {}
      }
    },
    toggleHold(assignment) {
      const newHoldState = !assignment.on_hold;
      axios.patch(`/assignments/${assignment.id}/update`, {
        field: 'on_hold',
        on_hold: newHoldState
      }).then(() => {
        assignment.on_hold = newHoldState;
        this.$toast.info(newHoldState ? `${assignment.name} put on hold.` : `${assignment.name} hold released.`);
      });
    },
    canToggleAddon(assignment) {
      return !assignment.started_at && !assignment.completed_at && !assignment.has_open_qa;
    },
    toggleAddon(assignment, field) {
      const newVal = !assignment[field];
      const label = field === 'poc' ? 'POC' : 'Review';
      axios.patch(`/assignments/${assignment.id}/update`, {
        field: field,
        [field]: newVal
      }).then(({data}) => {
        assignment[field] = data[field];
        assignment.cost = data.cost;
        this.reTotal();
        this.$toast.info(newVal ? `${label} added to ${assignment.name}.` : `${label} removed from ${assignment.name}.`);
      }).catch((e) => {
        this.$toast.error(e.response?.data?.errors?.[0]?.[0] || `Could not update ${label}.`);
      });
    },
    canToggleUrgent(assignment) {
      return !assignment.completed_at && !assignment.has_open_qa;
    },
    toggleUrgent(assignment) {
      const newUrgent = !assignment.urgent;
      axios.patch(`/assignments/${assignment.id}/update`, {
        field: 'urgent',
        urgent: newUrgent
      }).then(({data}) => {
        assignment.urgent = data.urgent;
        assignment.cost = data.cost;
        this.reTotal();
        this.$toast.info(newUrgent ? `${assignment.name} marked as urgent.` : `${assignment.name} urgent removed.`);
      });
    },
    canDelete(assignment) {
      const status = assignment.status;
      return status === 'Not Started' || status.includes('Sent to Problems');
    },
    canReopen(assignment) {
      return assignment.completed_at && !assignment.has_open_qa;
    },
    toggleReopen(id) {
      if (this.reopenTarget === id) {
        this.reopenTarget = null;
        this.reopenComment = '';
      } else {
        this.reopenTarget = id;
        this.reopenComment = '';
      }
    },
    reopenAssignment(assignment) {
      if (!this.reopenComment.trim()) return;

      this.reopening = assignment.id;
      axios.post(`/assignments/${assignment.id}/reopen`, {
        comment: this.reopenComment
      }).then(() => {
        assignment.completed_at = null;
        assignment.status = 'Started';
        this.reopenTarget = null;
        this.reopenComment = '';
        this.$toast.success(`${assignment.name} reopened.`);
      }).catch(error => {
        console.error("Reopen failed:", error);
        this.$toast.error("Failed to reopen assignment.");
      }).finally(() => {
        this.reopening = null;
      });
    },
    reopenPreviousAssignment(assignment) {
      if (!this.reopenComment.trim()) return;

      this.reopening = assignment.id;
      axios.post(`/assignments/${assignment.id}/reopen`, {
        comment: this.reopenComment
      }).then(() => {
        assignment.completed_at = null;
        this.reopenTarget = null;
        this.reopenComment = '';
        this.$toast.success(`${assignment.name} reopened.`);
      }).catch(error => {
        console.error("Reopen failed:", error);
        this.$toast.error("Failed to reopen assignment.");
      }).finally(() => {
        this.reopening = null;
      });
    },
    previousProblems(assignment) {
      const problems = assignment.problems || [];
      if (problems.length < 2) return [];
      // All problems except the latest (matched by problem_id, which the API
      // exposes as the most recent problem). Order: oldest first.
      return problems
        .filter(p => p.id !== assignment.problem_id)
        .slice()
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    },
    isProblemHistoryOpen(assignmentId) {
      return this.expandedProblems.includes(assignmentId);
    },
    toggleProblemHistory(assignmentId) {
      const idx = this.expandedProblems.indexOf(assignmentId);
      if (idx === -1) {
        this.expandedProblems.push(assignmentId);
      } else {
        this.expandedProblems.splice(idx, 1);
      }
    },
    resolveProblem(assignment) {
      if (!assignment.problem_id) return;

      this.resolving = assignment.id;
      axios.patch(`/problems/${assignment.problem_id}`, {
        resolved_comment: assignment.resolved_comment
      }).then(response => {
        // Find the assignment in our local list and update it
        const idx = this.sortedAssignments.findIndex(a => a.id === assignment.id);
        if (idx !== -1) {
          // The backend returns the fresh assignment with problems loaded
          const updatedAssignment = response.data;
          this.assignments = this.assignments.map(a => a.id === assignment.id ? { ...a, ...updatedAssignment } : a);
          this.$toast.success(`${assignment.name} problem resolved.`);
        }
      }).catch(error => {
        console.error("Resolution failed:", error);
        this.$toast.error("Failed to resolve problem.");
      }).finally(() => {
        this.resolving = null;
      });
    },
    canEdit(assignment, field = null) {
      if (assignment.has_open_qa) return false;
      
      // Comments are generally always editable by agencies
      if (field === 'comments') return true;
      
      const status = assignment.status;
      return status === 'Not Started' || status.includes('Sent to Problems') || assignment.problem || status === 'HOLD';
    },
    tryEdit(assignment, field) {
      if (this.canEdit(assignment, field)) {
        this.editField(assignment, field);
      } else {
        if (assignment.has_open_qa) {
          this.$toast.warning("Cannot edit while file is in QA Review.");
        } else if (assignment.completed_at) {
          this.$toast.warning("Completed assignments cannot be edited.");
        } else {
          this.$toast.warning("This assignment is currently in progress and cannot be edited.");
        }
      }
    },
    editField(assignment, field) {
      this.editing = { 
        id: assignment.id, 
        field: field, 
        originalValue: assignment[field] 
      };
    },
    cancelEdit(assignment) {
      if (this.editing.id === assignment.id && this.editing.field) {
        assignment[this.editing.field] = this.editing.originalValue;
      }
      this.editing = { id: null, field: null, originalValue: null };
    },
    updateField(assignment, field) {
      if (this.editing.id !== assignment.id || this.editing.field !== field) return;
      
      const value = assignment[field];
      axios.patch(`/assignments/${assignment.id}/update`, {
        field: field,
        [field]: value
      }).then(() => {
        this.$toast.success(`${field.replace('_', ' ')} updated.`);
      }).catch(e => {
        this.$toast.error("Update failed.");
      }).finally(() => {
        this.editing = { id: null, field: null, originalValue: null };
      });
    },
    allowFileUpload(id) {
      this.showFileUpload = id;
    },
    cancelAttach() {
      this.showFileUpload = "";
    },
    toggleFileUpload(id) {
      if (this.showFileUpload === id) {
        this.showFileUpload = "";
      } else {
        this.showFileUpload = id;
      }
    },
    triggerEntryAttachment() {
      this.$refs.entryAttachmentInput.click();
    },
    handleEntryAttachment(event) {
      const files = Array.from(event.target.files);
      this.entryAttachments = [...this.entryAttachments, ...files];
      // Reset input so the same file can be selected again if needed
      this.$refs.entryAttachmentInput.value = '';
    },
    removeEntryAttachment(index) {
      this.entryAttachments.splice(index, 1);
    },
    async uploadEntryAttachments(assignmentId, attachments) {
      for (const file of attachments) {
        let formData = new FormData();
        formData.append("attachment", file);
        try {
          const { data } = await axios.post(`/assignments/${assignmentId}/attachments`, formData);
          const assignment = this.assignments.find(a => a.id === assignmentId);
          if (assignment) {
            assignment.attachments.push(data);
          }
        } catch (error) {
          this.$toast.error(`Failed to upload ${file.name}`);
        }
      }
    },
    handleFileUpload(doc, assignmentId) {
      let data = new FormData();
      data.append("attachment", doc.file);
      axios.post(`/assignments/${assignmentId}/attachments`, data)
        .then(({ data }) => {
          const assignment = this.assignments.find(a => a.id === assignmentId);
          if (assignment) {
            assignment.attachments.push(data);
            this.$toast.success(`${doc.file.name} uploaded successfully`);
          }
        })
        .finally(() => {
          this.showFileUpload = "";
        });
    },
    removeAttachment(id, docI, assignmentId) {
      axios.delete(`/attachments/${id}`).then(() => {
        const assignment = this.assignments.find(a => a.id === assignmentId);
        if (assignment) {
          assignment.attachments.splice(docI, 1);
        }
      });
    },
    isImageFile(file) {
      return file.type.startsWith('image/');
    },
    isImageDoc(doc) {
      return doc.type.startsWith('image/');
    },
    getLocalFileUrl(file) {
      return URL.createObjectURL(file);
    },
    getUploadedFileUrl(doc, assignmentId) {
      return `/assignments/${assignmentId}/view/${doc.id}`;
    },
    viewLocalFile(file) {
      window.open(this.getLocalFileUrl(file), '_blank');
    },
    viewUploadedFile(doc, assignmentId) {
      window.open(this.getUploadedFileUrl(doc, assignmentId), '_blank');
    },
    getFileIcon(filename) {
      const ext = this.getFileExt(filename);
      switch (ext) {
          case 'pdf': return 'fas fa-file-pdf text-red-500';
          case 'doc':
          case 'docx': return 'fas fa-file-word text-blue-500';
          case 'xls':
          case 'xlsx': return 'fas fa-file-excel text-green-500';
          case 'csv': return 'fas fa-file-csv text-green-600';
          case 'txt': return 'fas fa-file-alt text-gray-500';
          case 'jpg':
          case 'jpeg':
          case 'png': return 'fas fa-file-image text-purple-500';
          default: return 'fas fa-file text-gray-400';
      }
    },
    getFileExt(filename) {
      return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
    },
    triggerFileInput() {
      this.$refs.csvInput.click();
    },
    handleCsvUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => this.parseCsvContent(e.target.result);
        reader.readAsText(file);
      }
    },
    parseCsvContent(content) {
      const lines = content.split(/\r?\n/).filter(line => line.trim());
      if (lines.length < 1) return;
      
      const assignments = lines.map(line => {
          const parts = line.split(',').map(p => p.trim());
          if (parts.length < 3) return null;
          return {
              name: parts[0],
              document_type: this.normalizeDocumentType(parts[1]),
              date: this.normalizeDate(parts[2]),
              comments: parts[3] || '',
              review: 0, poc: 0, urgent: 0, pro: 0
          };
      }).filter(a => a && a.document_type && a.date);

      this.batchAddAssignments(assignments);
    },
    normalizeDocumentType(type) {
      const normalized = type.toLowerCase().trim();
      return this.documentTypeMap[normalized] || null;
    },
    normalizeDate(dateStr) {
      for (const format of this.dateFormats) {
        const parsed = moment(dateStr, format, true);
        if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
      }
      return null;
    },
    async batchAddAssignments(batch) {
        for (const a of batch) {
            try {
                const { data } = await axios.post(`/agencies/${this.agency_id}/orders/${this.order_id}/assignments`, a);
                this.assignments.push(data);
                this.total += parseFloat(data.cost || 0);
            } catch (e) {}
        }
        this.$toast.success("CSV Import complete.");
    },
    togglePreviousOrder(orderId) {
      const idx = this.expandedOrders.indexOf(orderId);
      if (idx === -1) {
        this.expandedOrders.push(orderId);
      } else {
        this.expandedOrders.splice(idx, 1);
      }
    },
    handleGlobalKeyDown(e) {
      if (e.key === 'Escape') {
        if (this.showStatusFilter) this.showStatusFilter = false;
        if (this.showSettings) this.showSettings = false;
      }
    },
    handleGlobalClick(e) {
      if (this.showStatusFilter && this.$refs.statusFilterContainer && !this.$refs.statusFilterContainer.contains(e.target)) {
        this.showStatusFilter = false;
      }
      if (this.showSettings && this.$refs.settingsContainer && !this.$refs.settingsContainer.contains(e.target)) {
        this.showSettings = false;
      }
    }
  },
  computed: {
    urgentTooltip() {
      const cost = this.pricing ? this.pricing.urgent : 0;
      const costLine = cost > 0 ? `+$${cost} per assignment` : 'No additional cost';
      return `Add as Urgent\nPlaces at top of coder queue within your assignments\n${costLine}`;
    },
    reviewTooltip() {
      const cost = this.pricing ? this.pricing.review : 20;
      return `Coder will perform a post-coding review of the OASIS\n+$${cost} per assignment`;
    },
    pocTooltip() {
      const cost = this.pricing ? this.pricing.poc : 10;
      return `Coder will create a POC for the episode of care\n+$${cost} per assignment`;
    },
    reviewUsesDefaults() {
      return !!(this.agency.review_default_types && this.agency.review_default_types.length);
    },
    pocUsesDefaults() {
      return !!(this.agency.poc_default_types && this.agency.poc_default_types.length);
    },
    showReviewCol() {
      return !this.agency.review_disabled && this.visibleCols.review;
    },
    showPocCol() {
      return !this.agency.poc_disabled && this.visibleCols.poc;
    },
    sortedAssignments() {
      let filtered = [...this.assignments];
      
      if (this.selectedStatuses.length > 0) {
        filtered = filtered.filter(a => {
          const displayStatus = a.has_open_qa ? 'Internal Review' : (a.on_hold ? 'HOLD' : a.status);
          return this.selectedStatuses.includes(displayStatus);
        });
      }
      
      return filtered.sort((a, b) => b.id - a.id);
    },
    uniqueStatuses() {
      const statuses = this.assignments.map(a => 
        a.has_open_qa ? 'Internal Review' : (a.on_hold ? 'HOLD' : a.status)
      );
      return [...new Set(statuses)].sort();
    },
    dateFormats() {
      return ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM/DD/YY', 'M/D/YYYY', 'M/D/YY', 'MM-DD-YYYY', 'MM-DD-YY', 'MMM DD, YYYY', 'MMMM DD, YYYY'];
    },
    documentTypeMap() {
      return {
        'soc': 'SOC', 'start of care': 'SOC', 'roc': 'ROC', 'resumption': 'ROC', 'recert': 'Recert', 'rec': 'Recert',
        'roc/rec': 'ROC/Recert', 'roc/recert': 'ROC/Recert', 'follow-up': 'Follow-up', 'fu': 'Follow-up', 'sna': 'SNA'
      };
    },
    colSpans() {
      // Deprecated in favor of gridStyle
      return {};
    },
    gridStyle() {
      // Base Grid: 
      // Name (fr), Type (80px), Date (80px), Hold (50px), Status (140px), Options (60px)
      // Optional: Submitted (100px), Completed (100px), Problems (200px), Comments (200px)

      let columns = [];
      let minWidth = 150 + 80 + 80 + 50 + 160 + 80; // Base: 150(name) + other fixed cols
      
      // Name: Flexible
      columns.push('minmax(150px, 1fr)'); 
      
      // Type
      columns.push('80px');
      
      // Date
      columns.push('80px');
      
      // Hold
      columns.push('50px');

      // Add-on columns (only when the agency offers them)
      if (this.showReviewCol) {
        columns.push('55px');
        minWidth += 55;
      }
      if (this.showPocCol) {
        columns.push('55px');
        minWidth += 55;
      }
      
      // Optionals
      if (this.visibleCols.submitted) {
        columns.push('100px');
        minWidth += 100;
      }
      if (this.visibleCols.completed) {
        columns.push('100px');
        minWidth += 100;
      }
      if (this.visibleCols.problems) {
        columns.push('240px'); // Slightly wider for the new resolution UI
        minWidth += 240;
      }
      if (this.visibleCols.comments) {
        columns.push('200px');
        minWidth += 200;
      }
      
      // Status
      columns.push('160px');
      
      // Options
      columns.push('80px'); // Slightly wider for spacing

      return {
        'grid-template-columns': columns.join(' '),
        'min-width': `${minWidth}px`
      };
    }
  },
  directives: {
    focus: {
      inserted(el) {
        el.focus();
      }
    }
  }
};
</script>
